<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">
            Crear CSR para la llave {{ keyAlias || keyId }}
          </v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <v-col cols="12">
                  <h4 class="text-button text-primary">Identidad del titular</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.commonname"
                          label="Nombre común (CN)"
                          prepend-inner-icon="mdi-account"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('commonname')"
                          @update:model-value="errors.clear('commonname')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.serialnumber"
                          label="Número de serie (RUT)"
                          prepend-inner-icon="mdi-card-account-details"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('serialnumber')"
                          @update:model-value="errors.clear('serialnumber')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.email"
                          label="Email"
                          prepend-inner-icon="mdi-email"
                          :disabled="saving"
                          :rules="[rules.required, rules.email]"
                          :error-messages="errors.for('email')"
                          @update:model-value="errors.clear('email')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col cols="12">
                  <h4 class="text-button text-primary">Organización y ubicación</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.organization"
                          label="Organización (O)"
                          prepend-inner-icon="mdi-domain"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('organization')"
                          @update:model-value="errors.clear('organization')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.organization_unit"
                          label="Unidad organizacional (OU, opcional)"
                          prepend-inner-icon="mdi-sitemap"
                          :disabled="saving"
                          :error-messages="errors.for('organization_unit')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.country"
                          label="País (C)"
                          prepend-inner-icon="mdi-flag"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('country')"
                          hint="Código de dos letras, p. ej. CL."
                          persistent-hint
                          maxlength="2"
                          @update:model-value="errors.clear('country')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.state"
                          label="Región (ST)"
                          prepend-inner-icon="mdi-map"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('state')"
                          @update:model-value="errors.clear('state')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.locality"
                          label="Comuna (L, opcional)"
                          prepend-inner-icon="mdi-map-marker"
                          :disabled="saving"
                          :error-messages="errors.for('locality')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col v-if="requiresPin" cols="12">
                  <h4 class="text-button text-primary">Llave encapsulada</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-text-field
                          v-model="data.pin"
                          label="PIN"
                          prepend-inner-icon="mdi-form-textbox-password"
                          :disabled="saving"
                          :rules="[rules.required, rules.pin]"
                          :error-messages="errors.for('pin')"
                          counter="4"
                          maxlength="4"
                          hint="La llave está encapsulada: su PIN es necesario para firmar el CSR. Máximo 4 caracteres."
                          persistent-hint
                          @update:model-value="errors.clear('pin')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions>
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
                :disabled="loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Generar CSR
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                class="mx-1"
                :disabled="saving"
                @click="router.push(`/admin/keys/${keyId}/csr`)"
              >
                Cancelar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { KeyCsrPayload } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";

const route = useRoute();
const router = useRouter();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const keyId = computed(() => Number(route.params.key_id));

const loading = ref(true);
const saving = ref(false);
const keyAlias = ref("");
/** Una llave encapsulada necesita su PIN para poder firmar el CSR. */
const requiresPin = ref(false);

const data = ref({
  commonname: "",
  serialnumber: "",
  email: "",
  organization: "",
  organization_unit: "",
  country: "CL",
  state: "",
  locality: "",
  pin: "",
});

const rules = {
  required: (val: unknown) => !!val || "Campo requerido.",
  email: (val: unknown) => /.+@.+/.test(String(val ?? "")) || "Email inválido.",
  pin: (val: unknown) =>
    !val || String(val).length <= 4 || "El PIN admite como máximo 4 caracteres.",
};

async function load() {
  try {
    const key = await banco.admin.keys.get(keyId.value);
    keyAlias.value = key.alias ?? "";
    requiresPin.value = key.type === "WRAPPED";
  } catch (err) {
    report(err, { title: "Llave", fallback: "Ocurrió un error al recuperar la llave." });
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;

  const payload: KeyCsrPayload = {
    commonname: data.value.commonname,
    serialnumber: data.value.serialnumber,
    email: data.value.email,
    organization: data.value.organization,
    organization_unit: data.value.organization_unit || undefined,
    country: data.value.country,
    state: data.value.state,
    locality: data.value.locality || undefined,
    pin: requiresPin.value ? data.value.pin : undefined,
  };

  try {
    // Devuelve un CsrDTO, no una llave: en bancojs 1.x estaba mal tipado.
    const csr = await banco.admin.keys.createCsr(keyId.value, payload);
    notifications.success(`Se generó el CSR ${csr.id}.`, "Creado");
    router.push(`/admin/keys/${keyId.value}/csr`);
  } catch (err) {
    report(err, {
      title: "CSR",
      fallback: "Ocurrió un error al generar el CSR.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
